import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from 'store';
import { createServiceDate, patchServiceDate } from 'api/requests/contract-processing-requests';
import { getContractDateDetailAction, bulkConfirmServiceDatesAction } from 'store/actions/contract-processing-actions';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { ContractDetailPanelData, DetailPanelFormValues } from '../types';
import { SUCCESS_MESSAGE_TIMEOUT } from '../constants';

const EMPTY_FORM: DetailPanelFormValues = {
  costCenterId: null,
  startWork: '',
  endWork: '',
  breaks: [],
  noShow: false,
  reasonForNoShow: '',
  costDeclarations: false,
  costDeclarationRows: [{ costDeclarationId: '', amount: 0, quantity: 0 }],
};

function applyNoShowHoursOverride(values: DetailPanelFormValues, data: ContractDetailPanelData): DetailPanelFormValues {
  if (!values.reasonForNoShow) return values;
  return {
    ...values,
    startWork: data.plannedStartWork ?? '',
    endWork: data.plannedEndWork ?? '',
    breaks: data.plannedStartBreak
      ? [{ startBreak: data.plannedStartBreak, endBreak: data.plannedEndBreak ?? '' }]
      : [],
  };
}

/** The reason on the service (employee-reported or last chosen by a manager), as the selector's value. */
function getPersistedNoShowReason(data: ContractDetailPanelData): string {
  if (data.noShowReasonDetail) return String(data.noShowReasonDetail.id);
  return data.reasonForNoShow?.toString() ?? '';
}

function computePrefill(data: ContractDetailPanelData): Partial<DetailPanelFormValues> {
  // Priority: ServiceDate > TimeRegistration > Planned > empty
  if (data.serviceDateStartWork !== null) {
    return {
      startWork: data.serviceDateStartWork,
      endWork: data.serviceDateEndWork ?? '',
      breaks: data.serviceDateStartBreak
        ? [{ startBreak: data.serviceDateStartBreak, endBreak: data.serviceDateEndBreak ?? '' }]
        : [],
    };
  }
  if (data.registeredStartTime !== null) {
    return {
      startWork: data.registeredStartTime,
      endWork: data.registeredEndTime ?? '',
      breaks: data.registeredBreaks.map(b => ({ startBreak: b.startTime, endBreak: b.endTime })),
    };
  }
  if (data.plannedStartWork !== null) {
    return {
      startWork: data.plannedStartWork,
      endWork: data.plannedEndWork ?? '',
      breaks: data.plannedStartBreak
        ? [{ startBreak: data.plannedStartBreak, endBreak: data.plannedEndBreak ?? '' }]
        : [],
    };
  }
  return { startWork: '', endWork: '', breaks: [] };
}

export const useContractDetailPanel = (contractDateId: number | null, companyId: number) => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<ContractDetailPanelData | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const form = useForm<DetailPanelFormValues>({ defaultValues: EMPTY_FORM });

  const clearFetchError = useCallback(() => setFetchError(null), []);
  const clearSaveError = useCallback(() => setSaveError(null), []);
  const clearSuccessMessage = useCallback(() => {
    setSuccessMessage(null);
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  }, []);

  // Fetch detail when contractDateId changes
  useEffect(() => {
    if (contractDateId === null) {
      setData(null);
      setFetchError(null);
      form.reset(EMPTY_FORM);
      return;
    }

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    setFetchError(null);
    setData(null);

    dispatch(getContractDateDetailAction({ contractDateId, companyId }))
      .unwrap()
      .then(response => {
        if (requestIdRef.current !== currentRequestId) return; // stale request
        setData(response);
        const prefill = computePrefill(response);
        form.reset({
          ...EMPTY_FORM,
          costCenterId: response.costCenterId,
          ...prefill,
          reasonForNoShow: getPersistedNoShowReason(response),
          costDeclarationRows:
            response.costDeclarations.length > 0
              ? response.costDeclarations.map(cd => ({
                  costDeclarationId: String(cd.costDeclarationId),
                  amount: cd.amount,
                  quantity: cd.quantity,
                }))
              : EMPTY_FORM.costDeclarationRows,
        });
      })
      .catch(error => {
        if (requestIdRef.current !== currentRequestId) return;
        if (error === 403) {
          setFetchError('CONTRACTS__NO_ACCESS_TO_VIEW');
        } else {
          setFetchError('CONTRACTS__COULD_NOT_LOAD_DETAILS');
        }
      });
  }, [contractDateId, companyId, dispatch, form]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const save = useCallback(async () => {
    if (!data || !companyId) return;

    const values = form.getValues();
    const correctedValues = applyNoShowHoursOverride(values, data);
    const hasNoShow = !!correctedValues.reasonForNoShow;
    const filteredCostDeclarations = correctedValues.costDeclarationRows
      .filter(row => row.costDeclarationId && row.amount > 0)
      .map(row => ({ ...row, costDeclarationId: row.costDeclarationId }));
    const payload = {
      startWork: correctedValues.startWork || undefined,
      endWork: correctedValues.endWork || undefined,
      startBreak: correctedValues.breaks[0]?.startBreak || '',
      endBreak: correctedValues.breaks[0]?.endBreak || '',
      noShow: hasNoShow || undefined,
      reasonForNoShow: correctedValues.reasonForNoShow || undefined,
      costDeclarations: filteredCostDeclarations.length > 0 ? filteredCostDeclarations : undefined,
    };

    try {
      if (data.serviceDateId !== null) {
        await patchServiceDate(data.serviceDateId, payload, companyId);
      } else {
        await createServiceDate({ contractDateId: data.contractDateId, ...payload }, companyId);
      }

      setSuccessMessage('saved');
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => {
        setSuccessMessage(null);
        successTimerRef.current = null;
      }, SUCCESS_MESSAGE_TIMEOUT);
    } catch (error) {
      setSaveError(extractErrorMessage(error));
      // Revert form to last persisted values
      if (data) {
        const prefill = computePrefill(data);
        form.reset({
          ...EMPTY_FORM,
          costCenterId: data.costCenterId,
          ...prefill,
          reasonForNoShow: getPersistedNoShowReason(data),
        });
      }
      throw error; // Let caller know save failed
    }
  }, [data, companyId, form]);

  const getCorrectedValues = useCallback((): DetailPanelFormValues => {
    const values = form.getValues();
    return data ? applyNoShowHoursOverride(values, data) : values;
  }, [form, data]);

  const saveAndConfirm = useCallback(async () => {
    if (!data || !companyId) return;

    await save();
    clearSuccessMessage();

    const result = await dispatch(
      bulkConfirmServiceDatesAction({
        request: {
          contractDateIds: [data.contractDateId],
          selectAll: false,
          excludedContractDateIds: [],
          week: null,
          year: null,
        },
        companyId,
      }),
    ).unwrap();

    const hasError = result.some(item => item.contractDateId === data.contractDateId);
    if (hasError) {
      throw new Error('Confirmation failed');
    }
  }, [data, companyId, save, clearSuccessMessage, dispatch]);

  return {
    data,
    fetchError,
    clearFetchError,
    saveError,
    clearSaveError,
    successMessage,
    clearSuccessMessage,
    form,
    save,
    saveAndConfirm,
    getCorrectedValues,
  };
};
